import { useEffect, useMemo, useState } from "react";
import { dict, getInitialLang, type Lang } from "../lib/i18n";
import { vibrate } from "../lib/telegram";

type Tab = {
  href: string;
  hash: string;
  icon: string;
  key: keyof typeof dict["ru"];
};

type Props = { className?: string; compact?: boolean };

const TABS: Tab[] = [
  { href: "#/",         hash: "#/",        icon: "💬", key: "nav_chat" },
  { href: "#/generate", hash: "#/generate",icon: "✨", key: "nav_generate" },
  { href: "#/gallery",  hash: "#/gallery", icon: "🖼️", key: "nav_my" },
  { href: "#/public",   hash: "#/public",  icon: "🌐", key: "public_title" },
];

export default function NavBar({ className = "", compact = false }: Props) {
  const [lang] = useState<Lang>(() => getInitialLang(null));
  const t = (k: keyof typeof dict["ru"]) => dict[lang][k];

  const [hash, setHash] = useState<string>(() => location.hash || "#/");
  useEffect(() => {
    const onHash = () => setHash(location.hash || "#/");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const active = useMemo(() => {
    const h = hash || "#/";
    if (h.startsWith("#/public")) return "#/public";
    if (h.startsWith("#/gallery")) return "#/gallery";
    if (h.startsWith("#/generate")) return "#/generate";
    return "#/";
  }, [hash]);

  const itemPad = compact ? "px-3 py-1.5 text-[11px]" : "px-3.5 py-1.5 text-sm";
  const wrapPad = compact ? "px-2 py-1" : "px-1 py-1";

  return (
    <nav
      className={`
        flex items-center gap-1.5 rounded-2xl ${wrapPad}
        bg-white/5 border border-white/10 backdrop-blur
        ${compact ? "justify-between w-full" : "w-max mx-auto"} 
        ${className}
      `}
      aria-label="Главная навигация"
    >
      {TABS.map((ti) => {
        const is = active === ti.hash;
        return (
          <a
            key={ti.hash}
            href={ti.href}
            onClick={() => vibrate()}     // ← 🔥 ВИБРАЦИЯ ПРИ КЛИКЕ
            aria-current={is ? "page" : undefined}
            className={
              "relative flex items-center justify-center gap-1 rounded-xl transition " +
              itemPad +
              " " +
              (is
                ? "text-white bg-white/10 shadow-inner"
                : "text-white/70 hover:text-white hover:bg-white/5")
            }
          >
            <span className="text-lg md:text-xl" aria-hidden>
              {ti.icon}
            </span>

            <span className="hidden md:inline text-[13px]">
              {t(ti.key)}
            </span>

            {is && (
              <span
                aria-hidden
                className="absolute -bottom-0.5 left-2 right-2 h-0.5
                           bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-emerald-400
                           rounded-full"
              />
            )}
          </a>
        );
      })}
    </nav>
  );
}
